import { useAiContext } from '@/hooks/useAiContext';
import { cn } from '@/lib/utils';
import { useAiAssistantStore, useDeployStore, useEditorStore, useUIStore } from '@/store';
import { useWorkspaceStore } from '@/store/workspaceStore';
import {
    AlertTriangle,
    Check,
    Loader2,
    Play,
    Rocket,
    Shield,
    TestTubes,
    Wand2,
    X,
    Zap,
} from 'lucide-react';
import { useCallback } from 'react';

/**
 * EditorToolbar — compact bar above the editor with the core loop actions.
 *
 * The primary loop: AI generate → edit → compile → deploy → iterate.
 * Every action in that loop should be one click from here.
 */
export default function EditorToolbar() {
  const { compileResult, isCompiling, isDeploying, compile } = useDeployStore();
  const { isLoading: aiLoading, sendMessage } = useAiAssistantStore();
  const { activeTabId, tabs } = useEditorStore();
  const { setActivePanel } = useUIStore();
  const { activeWorkspace } = useWorkspaceStore();
  const { getAiContext } = useAiContext();
  const workspaceId = activeWorkspace?.id ?? '';

  const activeTab = tabs.find((t) => t.id === activeTabId);
  const isSolidity = activeTab?.language === 'sol';

  const handleCompile = useCallback(() => {
    if (workspaceId) compile(workspaceId);
  }, [workspaceId, compile]);

  const handleDeploy = useCallback(() => {
    setActivePanel('deploy');
  }, [setActivePanel]);

  const handleAiAction = useCallback(
    (task: string, prompt: string) => {
      setActivePanel('ai');
      sendMessage(prompt, task, getAiContext());
    },
    [setActivePanel, sendMessage, getAiContext],
  );

  // Compile status indicator
  const compileStatus = compileResult ? (compileResult.success ? 'success' : 'error') : null;

  const errorCount = compileResult?.errors?.length ?? 0;
  const warningCount = compileResult?.warnings?.length ?? 0;

  return (
    <div className="flex items-center h-8 px-2 gap-1 bg-[#252526] border-b border-[#1e1e1e] select-none shrink-0">
      {/* === CORE LOOP ACTIONS === */}

      {/* Compile */}
      <ToolbarButton
        icon={isCompiling ? Loader2 : Play}
        label="Compile"
        shortcut="Ctrl+Shift+B"
        onClick={handleCompile}
        disabled={isCompiling || !workspaceId}
        spinning={isCompiling}
        variant="default"
      />

      {/* Compile status badge */}
      {compileStatus && !isCompiling && (
        <div
          className={cn(
            'flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-medium',
            compileStatus === 'success'
              ? 'bg-green-500/15 text-green-400'
              : 'bg-red-500/15 text-red-400',
          )}
        >
          {compileStatus === 'success' ? (
            <>
              <Check className="w-3 h-3" />
              <span>OK</span>
            </>
          ) : (
            <>
              <X className="w-3 h-3" />
              <span>{errorCount} err</span>
            </>
          )}
          {warningCount > 0 && (
            <span className="text-yellow-400 ml-0.5">
              <AlertTriangle className="w-2.5 h-2.5 inline" /> {warningCount}
            </span>
          )}
        </div>
      )}

      {/* Deploy */}
      <ToolbarButton
        icon={isDeploying ? Loader2 : Rocket}
        label="Deploy"
        onClick={handleDeploy}
        disabled={isDeploying || !compileResult?.success}
        spinning={isDeploying}
        variant="primary"
      />

      {/* Separator */}
      <div className="w-px h-4 bg-[#444] mx-0.5" />

      {/* AI Quick Actions */}
      {isSolidity && (
        <>
          <ToolbarButton
            icon={Shield}
            label="Audit"
            onClick={() => handleAiAction('security_audit', 'Run a security audit on this code')}
            disabled={aiLoading}
            variant="danger"
          />
          <ToolbarButton
            icon={Zap}
            label="Gas"
            onClick={() =>
              handleAiAction('gas_optimize', 'Analyze gas usage and suggest optimizations')
            }
            disabled={aiLoading}
            variant="warning"
          />
          <ToolbarButton
            icon={TestTubes}
            label="Test"
            onClick={() =>
              handleAiAction('test_generate', 'Generate comprehensive tests for this contract')
            }
            disabled={aiLoading}
            variant="success"
          />
          <ToolbarButton
            icon={Wand2}
            label="Generate"
            onClick={() =>
              handleAiAction('contract_template', 'Generate a smart contract based on my open file')
            }
            disabled={aiLoading}
            variant="purple"
          />
        </>
      )}

      {/* Spacer */}
      <div className="flex-1" />

      {/* AI loading indicator */}
      {aiLoading && (
        <div className="flex items-center gap-1 text-[9px] text-purple-400 animate-pulse">
          <Loader2 className="w-3 h-3 animate-spin" />
          <span>AI thinking...</span>
        </div>
      )}
    </div>
  );
}

/* ================================================================== */
/*  ToolbarButton                                                     */
/* ================================================================== */

type ButtonVariant = 'default' | 'primary' | 'danger' | 'warning' | 'success' | 'purple';

const variantClasses: Record<ButtonVariant, string> = {
  default: 'text-[#cccccc] hover:bg-[#333] hover:text-white',
  primary: 'text-blue-400 hover:bg-blue-600/20 hover:text-blue-300',
  danger: 'text-red-400 hover:bg-red-600/20 hover:text-red-300',
  warning: 'text-yellow-400 hover:bg-yellow-600/20 hover:text-yellow-300',
  success: 'text-green-400 hover:bg-green-600/20 hover:text-green-300',
  purple: 'text-purple-400 hover:bg-purple-600/20 hover:text-purple-300',
};

function ToolbarButton({
  icon: Icon,
  label,
  shortcut,
  onClick,
  disabled,
  spinning,
  variant = 'default',
}: {
  icon: React.ElementType;
  label: string;
  shortcut?: string;
  onClick: () => void;
  disabled?: boolean;
  spinning?: boolean;
  variant?: ButtonVariant;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'flex items-center gap-1 px-2 py-1 rounded text-[10px] transition-colors',
        disabled ? 'opacity-40 cursor-not-allowed' : variantClasses[variant],
      )}
      title={shortcut ? `${label} (${shortcut})` : label}
    >
      <Icon className={cn('w-3.5 h-3.5', spinning && 'animate-spin')} />
      <span className="hidden sm:inline">{label}</span>
    </button>
  );
}
